package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.backendBaseUrl
import com.appsyde.shared.setAccessToken
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.koin.core.context.GlobalContext
import com.appsyde.shared.presentation.style.AppStylesLanding
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.style
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Serializable
private data class WorkspaceItem(val accountId: String, val name: String, val role: String)

@Serializable
private data class WorkspacesResponse(val workspaces: List<WorkspaceItem>)

@Serializable
private data class SelectWorkspaceRequest(val token: String, val accountId: String)

@Serializable
private data class SelectWorkspaceResponse(val accessToken: String)

@Serializable
data class OAuthCompleteMessage(val type: String, val accountId: String, val accessToken: String)

@Composable
fun ChooseWorkspaceScreen() {
    Style(AppStylesLanding)
    val client = remember { GlobalContext.get().get<HttpClient>() }
    val baseUrl = remember { backendBaseUrl() }
    val search = remember { window.location.search }
    val token = remember {
        val m = Regex("""[?&]token=([^&]*)""").find(search)
        m?.groupValues?.get(1)?.takeIf { it.isNotBlank() }.orEmpty()
    }

    var workspaces by remember { mutableStateOf<List<WorkspaceItem>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selecting by remember { mutableStateOf(false) }
    var selectedAccountId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(token) {
        if (token.isBlank()) {
            error = "Token manquant"
            loading = false
            return@LaunchedEffect
        }
        try {
            val response = client.get("$baseUrl/auth/workspaces?token=${(window.asDynamic().encodeURIComponent)(token)}") {
                accept(ContentType.Application.Json)
            }.body<WorkspacesResponse>()
            workspaces = response.workspaces
        } catch (e: Throwable) {
            error = e.message ?: "Impossible de charger les workspaces"
        }
        loading = false
    }

    LaunchedEffect(selectedAccountId) {
        val accountId = selectedAccountId ?: return@LaunchedEffect
        selecting = true
        try {
            val response = client.post("$baseUrl/auth/select-workspace") {
                contentType(ContentType.Application.Json)
                setBody(SelectWorkspaceRequest(token = token, accountId = accountId))
                accept(ContentType.Application.Json)
            }.body<SelectWorkspaceResponse>()
            setAccessToken(response.accessToken)
            window.asDynamic().localStorage.setItem("accountId", accountId)
            if (window.opener != null) {
                val msg = OAuthCompleteMessage("oauth_complete", accountId, response.accessToken)
                window.opener.asDynamic().postMessage(Json.encodeToString(msg), "*")
                window.close()
            } else {
                window.location.href = window.location.origin + "/"
            }
        } catch (_: Throwable) {
            selecting = false
            selectedAccountId = null
        }
    }

    fun onSelect(accountId: String) {
        if (selecting || token.isBlank()) return
        selectedAccountId = accountId
    }

    Div({ classes(AppStylesLanding.page) }) {
        Div({ classes(AppStylesLanding.container) }) {
            Div(attrs = {
                classes(AppStylesLanding.section, AppStylesLanding.card)
                style {
                    property("max-width", "520px")
                    property("margin", "80px auto")
                    property("padding", "32px")
                }
            }) {
                H2({ classes(AppStylesLanding.h2) }) {
                    Text("Choisir un workspace")
                }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Vous avez accès à plusieurs workspaces. Sélectionnez celui avec lequel vous souhaitez continuer.")
                }
                when {
                    loading -> P({ classes(AppStylesLanding.sectionLead) }) { Text("Chargement…") }
                    error != null -> P({ classes(AppStylesLanding.sectionLead) }) { Text(error!!) }
                    workspaces.isEmpty() -> P({ classes(AppStylesLanding.sectionLead) }) { Text("Aucun workspace disponible.") }
                    else -> Div(attrs = { style { property("margin-top", "20px") } }) {
                        workspaces.forEach { w ->
                            Button(attrs = {
                                classes(AppStylesLanding.btn)
                                style {
                                    property("width", "100%")
                                    property("margin-bottom", "10px")
                                    property("text-align", "left")
                                    property("justify-content", "flex-start")
                                }
                                onClick { onSelect(w.accountId) }
                                if (selecting) attr("disabled", "true")
                            }) {
                                Span { Text(w.name) }
                                Span(attrs = { style { property("margin-left", "8px"); property("opacity", "0.8"); property("font-size", "12px") } }) {
                                    Text(" · ${w.role}")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
